// Video URL detection and embedding utilities

export const VIDEO_PLATFORMS = {
  YOUTUBE: 'youtube',
  VIMEO: 'vimeo',
  DAILYMOTION: 'dailymotion',
  TWITCH: 'twitch'
};

/**
 * Detect if a URL is a video URL and return platform info
 * @param {string} url - The URL to check
 * @returns {object|null} - Platform info or null if not a video URL
 */
export const detectVideoUrl = (url) => {
  if (!url || typeof url !== 'string') return null;

  // YouTube patterns
  const youtubePatterns = [
    /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
    /(?:https?:\/\/)?(?:www\.)?(?:m\.youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/
  ];

  for (const pattern of youtubePatterns) {
    const match = url.match(pattern);
    if (match) {
      return {
        platform: VIDEO_PLATFORMS.YOUTUBE,
        videoId: match[1],
        originalUrl: url,
        embedUrl: `https://www.youtube.com/embed/${match[1]}?playsinline=1&rel=0&enablejsapi=1`,
        thumbnailUrl: `https://img.youtube.com/vi/${match[1]}/maxresdefault.jpg`
      };
    }
  }

  // Vimeo patterns
  const vimeoPatterns = [
    /(?:https?:\/\/)?(?:www\.)?vimeo\.com\/(\d+)/,
    /(?:https?:\/\/)?player\.vimeo\.com\/video\/(\d+)/
  ];

  for (const pattern of vimeoPatterns) {
    const match = url.match(pattern);
    if (match) {
      return {
        platform: VIDEO_PLATFORMS.VIMEO,
        videoId: match[1],
        originalUrl: url,
        embedUrl: `https://player.vimeo.com/video/${match[1]}`,
        thumbnailUrl: `https://vumbnail.com/${match[1]}.jpg`
      };
    }
  }

  // Dailymotion patterns
  const dailymotionPattern = /(?:https?:\/\/)?(?:www\.)?dailymotion\.com\/video\/([a-zA-Z0-9]+)/;
  const dailymotionMatch = url.match(dailymotionPattern);
  if (dailymotionMatch) {
    return {
      platform: VIDEO_PLATFORMS.DAILYMOTION,
      videoId: dailymotionMatch[1],
      originalUrl: url,
      embedUrl: `https://www.dailymotion.com/embed/video/${dailymotionMatch[1]}`,
      thumbnailUrl: `https://www.dailymotion.com/thumbnail/video/${dailymotionMatch[1]}`
    };
  }

  // Twitch patterns
  const twitchPatterns = [
    /(?:https?:\/\/)?(?:www\.)?twitch\.tv\/videos\/(\d+)/,
    /(?:https?:\/\/)?clips\.twitch\.tv\/([a-zA-Z0-9_-]+)/
  ];

  for (const pattern of twitchPatterns) {
    const match = url.match(pattern);
    if (match) {
      const isClip = url.includes('clips.twitch.tv');
      return {
        platform: VIDEO_PLATFORMS.TWITCH,
        videoId: match[1],
        originalUrl: url,
        embedUrl: isClip 
          ? `https://clips.twitch.tv/embed?clip=${match[1]}&parent=localhost`
          : `https://player.twitch.tv/?video=${match[1]}&parent=localhost`,
        thumbnailUrl: null // Twitch doesn't provide direct thumbnail URLs
      };
    }
  }

  return null;
};

/**
 * Check if a URL is a valid video URL
 * @param {string} url - The URL to validate
 * @returns {boolean} - True if valid video URL
 */
export const isVideoUrl = (url) => {
  return detectVideoUrl(url) !== null;
};

/**
 * Get embed URL from original video URL
 * @param {string} url - Original video URL
 * @returns {string|null} - Embed URL or null if not a video
 */
export const getEmbedUrl = (url) => {
  const videoInfo = detectVideoUrl(url);
  return videoInfo ? videoInfo.embedUrl : null;
};

/**
 * Get video thumbnail URL
 * @param {string} url - Original video URL
 * @returns {string|null} - Thumbnail URL or null
 */
export const getVideoThumbnail = (url) => {
  const videoInfo = detectVideoUrl(url);
  return videoInfo ? videoInfo.thumbnailUrl : null;
};

/**
 * Validate and normalize video URL
 * @param {string} url - URL to validate
 * @returns {object} - Validation result with normalized URL
 */
export const validateVideoUrl = (url) => {
  if (!url || typeof url !== 'string') {
    return { isValid: false, error: 'URL is required' };
  }

  // Add protocol if missing
  let normalizedUrl = url;
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    normalizedUrl = `https://${url}`;
  }

  const videoInfo = detectVideoUrl(normalizedUrl);
  
  if (!videoInfo) {
    return { 
      isValid: false, 
      error: 'Please enter a valid video URL from YouTube, Vimeo, Dailymotion, or Twitch' 
    };
  }

  return {
    isValid: true,
    normalizedUrl,
    videoInfo
  };
};

/**
 * Get platform display name
 * @param {string} platform - Platform identifier
 * @returns {string} - Human readable platform name
 */
export const getPlatformDisplayName = (platform) => {
  const displayNames = {
    [VIDEO_PLATFORMS.YOUTUBE]: 'YouTube',
    [VIDEO_PLATFORMS.VIMEO]: 'Vimeo',
    [VIDEO_PLATFORMS.DAILYMOTION]: 'Dailymotion',
    [VIDEO_PLATFORMS.TWITCH]: 'Twitch'
  };
  
  return displayNames[platform] || 'Video';
};